// @ts-check
// =============================================================================
// js/core/reconcile.js — the tiny keyed-diff primitive (pseudo-React).
//
// WHAT THIS IS
//   The matching half of a React-style reconciler, sized for this game. A
//   screen keeps a list of MOUNTED entries (each carrying the render `key` it
//   was built from). When new state arrives, diffKeyed() matches new items to
//   mounted entries by key — order-preserving, duplicate-safe — and reports:
//
//     kept:    [{ entry, newIndex }]  reuse the mounted object (maybe move it)
//     added:   [newIndex, …]          build these
//     removed: [entry, …]             destroy these
//
//   The KEY captures everything that affects the pixels ("what would I look
//   like"), so identical key = the mounted object is already correct and is
//   NEVER torn down. That's the whole anti-flicker contract: unchanged
//   elements survive every update untouched.
//
// WHO USES IT
//   The duel's hand (a keyed list with duplicates — two copies of the same
//   card share a key, so matching is multiset-style). The board reconciles
//   per fixed slot and doesn't need list matching; bars update fields in
//   place. Any future screen with a keyed collection can use this.
// =============================================================================

/**
 * Match new keys against mounted entries, order-preserving + duplicate-safe.
 *
 * @template T
 * @param {Array<T & {key: string}>} mounted  Currently mounted entries.
 * @param {string[]} newKeys                  Keys for the incoming state, in order.
 * @returns {{
 *   kept: Array<{entry: T & {key: string}, newIndex: number}>,
 *   added: number[],
 *   removed: Array<T & {key: string}>,
 * }}
 */
export function diffKeyed(mounted, newKeys) {
	// Bucket mounted entries by key; consume each bucket FIFO so duplicate
	// keys pair up in display order (stable for hands with repeated cards).
	/** @type {Map<string, Array<any>>} */
	const buckets = new Map();
	for (const entry of mounted) {
		const bucket = buckets.get(entry.key);
		if (bucket) {
			bucket.push(entry);
		} else {
			buckets.set(entry.key, [entry]);
		}
	}

	const kept = [];
	const added = [];
	for (let newIndex = 0; newIndex < newKeys.length; newIndex++) {
		const bucket = buckets.get(newKeys[newIndex]);
		if (bucket && bucket.length) {
			kept.push({ entry: bucket.shift(), newIndex });
		} else {
			added.push(newIndex);
		}
	}

	// Whatever is left in the buckets has no home in the new state.
	const removed = [];
	for (const bucket of buckets.values()) {
		removed.push(...bucket);
	}
	return { kept, added, removed };
}
